import { createFileRoute, Link } from '@tanstack/react-router';
import { Mic, Dices, Globe2, ShieldCheck } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { CaptionStrip, PushToTalk } from '@/components/site/mocks';
import { GAME_MASTERS, GameMasterCard } from '@/components/site/GameMasterCard';
import heroEmblem from '@/assets/hero-everwake-brand.jpg';
import ashfordImage from '@/assets/campaign-ashford.jpg';
import chapelImage from '@/assets/campaign-chapel.jpg';
import beneathImage from '@/assets/campaign-beneath.jpg';

const highlights = [
  {
    icon: Mic,
    title: 'Play with your voice',
    body: 'Say what you do and hear the world answer. A text box is always there when you prefer to type.',
  },
  {
    icon: Dices,
    title: 'Dice you can prove',
    body: 'Every roll is sealed before play and verifiable in your browser when the session ends.',
  },
  {
    icon: Globe2,
    title: 'A living kingdom',
    body: 'Ashford moves on between sessions, and you get a clear briefing before you speak again.',
  },
  {
    icon: ShieldCheck,
    title: 'Rules you know',
    body: '5E-compatible play built on the SRD, with a game master that keeps every ruling consistent.',
  },
];

export const Route = createFileRoute('/_site/')({
  head: () => ({
    meta: [
      { title: 'Everwake — Speak, and the world wakes' },
      {
        name: 'description',
        content:
          'A solo voice-first role-playing game with an AI game master that remembers every choice, where every roll counts and every death is final. 5E-compatible. 18+.',
      },
      { property: 'og:title', content: 'Everwake — Speak, and the world wakes' },
      {
        property: 'og:description',
        content:
          'A solo voice-first role-playing game with an AI game master. Enter a world that wakes when you play, with verifiable dice and total immersion. 18+.',
      },
      { property: 'og:type', content: 'website' },
      { name: 'twitter:card', content: 'summary_large_image' },
    ],
  }),
  component: Landing,
});

const pillars = [
  {
    title: 'A world that wakes when you play',
    body: 'Each session moves Ashford forward around your choices. When you return, prices, rumours, factions and relationships are reconciled into a clear briefing before you speak.',
  },
  {
    title: 'Dice you can prove',
    body: 'The server commits to a secret seed and publishes its hash before play. At session end the seed is revealed and every roll can be recomputed in your browser.',
  },
  {
    title: 'A story with real consequences',
    body: 'Death saves exactly as the rules write them, absolute honesty in every roll, and a fair warning before any deadly beat you have full agency over. Your campaign, journal and Codex survive you.',
  },
  {
    title: 'A story that finds its own rhythm',
    body: 'Conversations, discoveries and tense choices lead the evening. When conflict erupts naturally, the room becomes a tactical grid built from the fiction around you.',
  },
];

const campaigns = [
  {
    id: 'ashford',
    title: 'The Kingdom of Ashford',
    sessions: 'One session',
    hook: 'A missing shipment, and talk on the north road that dies when you walk past.',
    image: ashfordImage,
    alt: 'A rain-darkened Ashford road with an overturned cart and lanterns near stone houses',
  },
  {
    id: 'chapel',
    title: 'The Chapel Bell',
    sessions: 'Three sessions',
    hook: 'The chapel bell has not rung in three mornings, and the rope marks say it was lowered.',
    image: chapelImage,
    alt: 'A misty chapel courtyard with a silent bell tower and rope on wet flagstones',
  },
  {
    id: 'beneath',
    title: 'The Old Kingdom Beneath',
    sessions: 'Ten sessions',
    hook: 'A tremor moves the altar stone, and the draught from behind it is older than the kingdom.',
    image: beneathImage,
    alt: 'Ancient stone ruins opening beneath a cracked chapel altar in a lantern beam',
  },
];

function Landing() {
  return (
    <div>
      <section className="relative overflow-hidden border-b border-border">
        <img
          src={heroEmblem}
          width={1920}
          height={1088}
          alt="A dark dragon curled around a glowing violet die above misty mountains and a distant castle"
          className="pointer-events-none absolute inset-0 h-full w-full object-cover"
        />
        <div className="pointer-events-none absolute inset-0 bg-gradient-to-r from-background via-background/85 to-background/30" />
        <div className="relative mx-auto max-w-6xl px-5 py-20 md:py-28">
          <div>
            <p className="inline-flex items-center justify-center rounded-md bg-primary px-3 py-1.5 text-sm font-bold tracking-wide text-primary-foreground">
              18+
            </p>
            <h1 className="mt-6 font-display text-5xl leading-[1.02] font-extrabold md:text-6xl">
              A solo voice-first
              <br />
              <span className="text-primary">RPG open-world</span>
            </h1>
            <p className="mt-6 max-w-xl text-lg text-muted-foreground">
              Everwake is played out loud with an AI game master that remembers every choice. Provable dice, real stakes and a living kingdom, all 5E-compatible.
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-3">
              <Button asChild size="lg">
                <Link to="/playtest">Request a playtest pass</Link>
              </Button>
              <Button asChild variant="outline" size="lg">
                <Link to="/how-it-plays">How it plays</Link>
              </Button>
            </div>
            <div className="mt-8">
              <PushToTalk />
            </div>
          </div>

        </div>
      </section>

      <section className="border-b border-border bg-muted/60">
        <div className="mx-auto grid max-w-6xl gap-8 px-5 py-14 sm:grid-cols-2 lg:grid-cols-4">
          {highlights.map((h) => (
            <div key={h.title} className="lg:border-l lg:border-border lg:pl-6 lg:first:border-l-0 lg:first:pl-0">
              <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-accent text-primary">
                <h.icon className="h-5 w-5" />
              </span>
              <h2 className="mt-4 font-display text-lg font-bold">{h.title}</h2>
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{h.body}</p>
            </div>
          ))}
        </div>
      </section>


      <section className="mx-auto max-w-3xl px-5 py-20">
        <p className="rule-line">A minute at the table</p>
        <div className="mt-6 space-y-6">
          <CaptionStrip speaker="You">
            I keep my voice down. Who lowered the bell rope?
          </CaptionStrip>
          <CaptionStrip speaker="Sophia, game master">
            The sacristan's hands go still on the cloth. Outside, someone crosses the yard and does not look in. She says: ask the reeve, not me.
          </CaptionStrip>
          <CaptionStrip speaker="System" system>
            Insight: d20 + 3 vs DC 13 — sealed roll #18 &rarr; 9. Failure.
          </CaptionStrip>
          <CaptionStrip speaker="Sophia, game master">
            You believe her, and that is the mistake. She waits until you are through the door before she moves the ledger off the table.
          </CaptionStrip>
        </div>
      </section>

      <section className="border-y border-border/70 bg-card/40">
        <div className="mx-auto max-w-6xl px-5 py-20">
          <h2 className="font-display text-3xl">
            What makes an Everwake night memorable
          </h2>
          <div className="mt-10 grid gap-6 md:grid-cols-2">
            {pillars.map((p) => (
              <article
                key={p.title}
                className="rounded-xl border border-border bg-background/60 p-6 shadow-table"
              >
                <h3 className="font-display text-xl">{p.title}</h3>
                <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{p.body}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-3xl px-5 py-20 text-center">
        <p className="rule-line">Your evening in Ashford</p>
        <p className="mt-5 font-display text-2xl leading-relaxed md:text-3xl">
          One session pass gives you about two hours with your AI game master: room to explore, talk, take risks and reach a satisfying cliffhanger.
        </p>
        <Button asChild variant="link" className="mt-4">
          <Link to="/pricing">How pricing works</Link>
        </Button>
      </section>

      <section className="border-y border-border/70 bg-card/40">
        <div className="mx-auto max-w-6xl px-5 py-20">
          <h2 className="font-display text-3xl">Two game masters</h2>
          <div className="mt-8 grid gap-6 md:grid-cols-2">
            {GAME_MASTERS.map((master) => (
              <GameMasterCard key={master.id} master={master} />
            ))}
          </div>
          <Button asChild variant="link" className="mt-4 px-0">
            <Link to="/game-masters">Hear the difference</Link>
          </Button>
        </div>
      </section>

      <section className="mx-auto max-w-6xl px-5 py-20">
        <h2 className="font-display text-3xl">One world, three campaigns</h2>
        <div className="mt-8 grid gap-6 md:grid-cols-3">
          {campaigns.map((c) => (
            <article key={c.id} className="overflow-hidden rounded-xl border border-border bg-card/60">
              <img
                src={c.image}
                width={1200}
                height={760}
                loading="lazy"
                alt={c.alt}
                className="aspect-[1.58] w-full object-cover"
              />
              <div className="p-6">
                <p className="rule-line">{c.sessions}</p>
                <h3 className="mt-3 font-display text-xl">{c.title}</h3>
                <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{c.hook}</p>
              </div>
            </article>
          ))}
        </div>
        <Button asChild variant="link" className="mt-4 px-0">
          <Link to="/campaigns">All three campaigns</Link>
        </Button>
      </section>

      <section className="border-t border-border/70 bg-card/60">
        <div className="mx-auto flex max-w-6xl flex-wrap items-center justify-between gap-6 px-5 py-14">
          <div>
            <h2 className="font-display text-2xl">
              Everwake is in controlled playtests.
            </h2>
            <p className="mt-2 text-sm text-muted-foreground">
              Sessions open by pass only, and every pass is issued by a person.
            </p>
          </div>
          <Button asChild size="lg">
            <Link to="/playtest">Request a playtest pass</Link>
          </Button>
        </div>
      </section>
    </div>
  );
}
